import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Input from "../../components/global/Input";
import Button from "../../components/global/Button";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { daysOptions } from "../../data/options";
import { selectStyle, selectTheme } from "../../styles/selectStyles";
import addDark from "../../assets/icons/addDark.svg";
import WorkingHoursTable from "./WorkingHoursTable";
import { useEditClubMutation } from "../../lib/redux/services/sections/Clubs";
import { toast } from "react-toastify";
import { hasOverlappingHours, toastMessage } from "../../utils/utils";
import { convertTo12HourFormat } from "../../utils/utils";
import ValidateError from "../global/ValidateError";
import SelectOption from "../global/SelectOption";
import { useGetDictionary } from "../../hooks/useGetDictionary";
import { DictionaryType } from "../../types";
type WorkingHour = {
  days: number[];
  hour_from: string;
  hour_to: string;
};

const ClubInfoForm1 = () => {
  const { organization, inputs, shared, options }: DictionaryType =
    useGetDictionary();
  const [workingHours, setWorkingHours] = useState<WorkingHour[]>([]);
  const { club_id } = useParams();
  const [hasOverlapping, setHasOvelapping] = useState(false);
  const {
    control,
    handleSubmit,
    reset, // To reset form values
    formState: { errors },
    watch,
  } = useForm<WorkingHour>();

  const navigate = useNavigate();

  const [editClub] = useEditClubMutation();

  const onSubmit = async () => {
    if (workingHours.length === 0) {
      return;
    }
    try {
      const formatedWorkingHours = workingHours?.map((work) => ({
        ...work,
        hour_from: convertTo12HourFormat(work.hour_from),
        hour_to: convertTo12HourFormat(work.hour_to),
      }));
      const data = await toast.promise(
        editClub({
          edits: { working_hours: formatedWorkingHours },
          id: club_id,
        }).unwrap(),

        toastMessage()
      );

      navigate(`/auth/club-info-2/${club_id}`);
    } catch (error) {}
  };

  const addWorkingHour = (data: WorkingHour) => {
    const updatedWorkingHours: WorkingHour = data.days.includes(7)
      ? {
          ...data,
          days: [0, 1, 2, 3, 4, 5, 6],
        }
      : data.days.includes(8)
      ? {
          ...data,
          days: [0, 1, 2, 3, 4],
        }
      : data;

    setWorkingHours((prev) => {
      if (hasOverlappingHours([...prev, updatedWorkingHours])) {
        setHasOvelapping(true);
        toast.error(shared.working_hours_overlapping_message);
        return prev;
      } else {
        reset({ days: [], hour_from: "", hour_to: "" });
        setHasOvelapping(false);
        return [...prev, updatedWorkingHours];
      }
    });
  };

  const removeWorkingHour = (index: number) => {
    setWorkingHours((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 md:space-y-4 max-w-[814px] px-5"
    >
      <div className="flex flex-row gap-5">
        <div className="w-full inline-flex items-start gap-5">
          <div className="w-[200%]">
            <label className="block text-sm mb-2">{inputs.days}</label>
            <Controller
              control={control}
              name="days"
              rules={{
                required: workingHours.length === 0 && "Please select days",
              }}
              render={({ field }) => (
                <Select
                  components={{
                    IndicatorSeparator: () => null,
                    Option: SelectOption,
                  }}
                  {...field}
                  onChange={(selected) =>
                    field.onChange(
                      selected
                        ? selected.map((option: any) => option.value)
                        : []
                    )
                  }
                  value={
                    field.value
                      ? daysOptions(options).filter((option) =>
                          field.value.includes(option.value)
                        )
                      : []
                  }
                  isMulti
                  styles={selectStyle}
                  classNamePrefix="react-select"
                  theme={selectTheme}
                  options={
                    watch(`days`)?.includes(7)
                      ? daysOptions(options).filter((day) => day.value == 7)
                      : watch(`days`)?.includes(8)
                      ? daysOptions(options).filter((day) => day.value == 8)
                      : watch(`days`)?.length > 0 &&
                        (!watch(`days`)?.includes(7) ||
                          !watch(`days`)?.includes(8))
                      ? daysOptions(options).filter(
                          (day) => day.value != 8 && day.value != 7
                        )
                      : daysOptions(options)
                  }
                  placeholder="Select"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                />
              )}
            />
            {errors.days && <ValidateError />}
          </div>

          <div className="w-full">
            <Controller
              control={control}
              name="hour_from"
              rules={{
                required: workingHours.length === 0 && "Hour From is required",
              }}
              render={({ field }) => (
                <Input
                  inputProps={{
                    ...field,
                    type: "time",
                    placeholder: "Hour From",
                  }}
                  label={inputs.hour_from}
                />
              )}
            />
            {errors.hour_from && <ValidateError />}
          </div>

          <div className="w-full">
            <Controller
              control={control}
              name="hour_to"
              rules={{
                required: workingHours.length === 0 && "Hour To is required",
              }}
              render={({ field }) => (
                <Input
                  inputProps={{
                    ...field,
                    type: "time",
                    placeholder: "Hour To",
                  }}
                  label={inputs.hour_to}
                />
              )}
            />
            {errors.hour_to && <ValidateError />}
          </div>
        </div>

        <div className="mt-8">
          <img loading="lazy"
            src={addDark}
            alt="Add"
            className="rounded cursor-pointer"
            onClick={handleSubmit(addWorkingHour)} // Use handleSubmit to validate before adding
          />
        </div>
      </div>
      <div className="text-lg font-bold mb-4 border-dotted border-b-primary border-b-2" />

      <WorkingHoursTable
        workingHours={workingHours}
        //@ts-ignore
        daysOptions={daysOptions(options)}
        removeWorkingHour={removeWorkingHour}
      />

      {/* Submit Button */}
      <div className="flex flex-row justify-center items-center pt-4 gap-10">
        <Button
          customeStyle="bg-white text-primary dark:text-white  w-full rounded-full  text-base py-2.5 border-primary border-2 "
          href="/auth/register"
          text={shared.back}
          width="max-w-[400px] w-full"
        />
        <Button
          customeStyle="bg-primary text-white w-full rounded-full text-base py-2.5 border-primary border-2 "
          text={shared.save_and_next}
          width="max-w-[400px] w-full"
          type="submit"
        />
      </div>
    </form>
  );
};

export default ClubInfoForm1;
